import { unwrapResult } from "@reduxjs/toolkit";
import CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import useScreenResolution from "../../../../hooks/useScreenResolution";
import { createCaseComplaint } from "../../../../redux/features/crm/caseComplaintSlice";
import { resetPolicyCancellationFlow } from "../../../../redux/features/motorInsurancePolicyCancellationFlowSlice";
import { policyView } from "../../../../redux/features/policyDetailsSlice";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import {
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON,
  POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW,
} from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/data";
import PersonalAccidentCancellationReasonPage from "./personalAccidentCancellationReasonPage";
import PersonalAccidentConfirmCancellationPage from "./personalAccidentConfirmCancellationPage";
import PersonalAccidentReviewCancellationPage from "./personalAccidentReviewCancellationPage";

const CancelPolicyPersonalAccident = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { policy_id } = useParams();
  const translate = useLanguageTranslate();
  const screenResolution = useScreenResolution();
  const { currentStep } = usePolicyCancellationFlow();

  // Selected reason state
  const [selectedReasonOut, setSelectedReasonOut] = useState("");

  // Policy decryption
  const secretKey = "i-hate-meej";
  const base64EncryptedValue = (policy_id || "")
    .split("-")
    .join("+")
    .split("_")
    .join("/");
  let originalValue = "";
  try {
    if (base64EncryptedValue) {
      const bytes = CryptoJS.AES.decrypt(base64EncryptedValue, secretKey);
      originalValue = bytes.toString(CryptoJS.enc.Utf8);
    }
  } catch (e) { originalValue = ""; }
  const policyNumber = originalValue;

  // Redux state
  const policyDetails = useSelector(
    (state) => state.policyDetails?.PolicyDetails
  );
  const policyDetailsStatus = useSelector(
    (state) => state.policyDetails?.status
  );
  const profileInfo = useSelector(selectProfileData);
  const caseComplaintStatus = useSelector(
    (state) => state.caseComplaint?.status
  );

  // Reset flow and load policy details on mount
  useEffect(() => {
    dispatch(resetPolicyCancellationFlow());

    // Load policy details if not already loaded
    if (
      policyDetails == null ||
      policyDetails?.PolicyHeader?.PolicyNumber !== policyNumber
    ) {
      dispatch(
        policyView({
          PolicyNumber: policyNumber,
          Channel: getClientSource(),
        })
      );
    }
  }, [dispatch, policyNumber]);

  // Navigation handler
  const handleNavigationToPolicyOverview = () => {
    navigate(
      `/personal-accident/policy-overview/${policy_id}?tabType=ManagePolicy`,
      {
        state: location.state,
      }
    );
  };

  // Handle final confirmation and API call
  const confirmedCancellation = async () => {
    const policyDetailsPartyInfo = policyDetails?.PolicyHeader?.Party;

    const payload = {
      FirstName:
        policyDetailsPartyInfo?.FirstName || profileInfo?.FirstName || "",
      LastName: policyDetailsPartyInfo?.LastName || profileInfo?.LastName || "",
      PhoneNumber:
        policyDetailsPartyInfo?.PhoneNumber || profileInfo?.PhoneNumber || "",
      Email: profileInfo?.Email || "",
      Subject: "Cancel Policy",
      Product: "Individual Personal Accident", // Personal Accident Insurance
      Lob: "MISC", // Personal Accident Insurance → MISC
      Descreption: `Policy to be cancelled. \nSelected Reason: ${selectedReasonOut}`,
      ClaimNumber: "",
      PolicyNumber: policyNumber,
      VNumber: "", // Empty string for personal accident insurance (not motor)
      Channel: getClientSource(),
    };

    try {
      const result = await dispatch(createCaseComplaint(payload));
      const unwrappedResult = unwrapResult(result);

      handleNavigationToPolicyOverview();
      toast.success(
        translate(
          "Your request for cancellation has been successfully sent",
          "تم إرسال طلب الإلغاء بنجاح"
        )
      );
    } catch (error) {
      toast.error(
        translate(
          "Error in sending cancel policy request",
          "خطأ في إرسال طلب إلغاء السياسة"
        )
      );
    }
  };

  // Render page based on current step
  const renderPage = () => {
    switch (currentStep?.name) {
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REVIEW:
        return (
          <PersonalAccidentReviewCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_REASON:
        return (
          <PersonalAccidentCancellationReasonPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            setSelectedReasonOut={setSelectedReasonOut}
          />
        );
      case POLICY_CANCELLATION_FLOW_POLICY_CANCELLATION_CONFIRMATION:
        return (
          <PersonalAccidentConfirmCancellationPage
            handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
            confirmedCancellation={confirmedCancellation}
            selectedReasonOut={selectedReasonOut}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[7%] ipad:h-[93vh] ipad:overflow-y-scroll">
      {renderPage()}
    </div>
  );
};

export default CancelPolicyPersonalAccident;
